import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { vitalsFiltersAreEqual, type VitalsFilters } from "../../hooks/useVitalsTable";
import { VitalsFilterConfigForm } from "./VitalsFilterConfigForm";
import { ColumnConfigForm, type ColumnConfigFormProps } from "../tables/ColumnConfigForm";
import type { UseTableViewConfigResult } from "../../hooks/useTableViewConfig";
import type { SortingState } from "@tanstack/react-table";
import { Button, cn, DateRangeField, DialogFooter, withKey } from "@tellescope/ui";
import { Calendar } from "lucide-react";

export interface VitalsTableConfigDialogContentProps {
  onApplyConfig: UseTableViewConfigResult<VitalsFilters>["onApplyConfig"];
  activeFilters: VitalsFilters;
  columnVisibility: ColumnConfigFormProps["columnVisibility"];
  orderedVisibleColumnIds: ColumnConfigFormProps["orderedVisibleColumnIds"];
  combinedFieldToColumnConfigMap: ColumnConfigFormProps["combinedFieldToColumnConfigMap"];
  columnConfigTitle: ColumnConfigFormProps["title"];
  sorting?: SortingState;
  setSorting?: Dispatch<SetStateAction<SortingState>>;
  providedIsDirty?: boolean;
  sourceOptions?: string[];
  onClearAll?: () => void;
  onClose: () => void;
  open?: boolean;
}

export const VitalsTableConfigDialogContent = withKey(
  ({
    onApplyConfig,
    activeFilters,
    columnVisibility,
    orderedVisibleColumnIds,
    combinedFieldToColumnConfigMap,
    columnConfigTitle,
    sorting,
    setSorting,
    providedIsDirty,
    sourceOptions,
    onClearAll,
    onClose,
    open: _,
  }: VitalsTableConfigDialogContentProps) => {
    const { t } = useTranslation("vitals");

    const [stagedColumnIds, setStagedColumnIds] = useState<string[]>(orderedVisibleColumnIds);
    const [stagedSorting, setStagedSorting] = useState<SortingState>(sorting ?? []);
    const [stagedFilters, setStagedFilters] = useState<VitalsFilters>(activeFilters);

    const isColumnsDirty = useMemo(() => {
      if (stagedColumnIds.length !== orderedVisibleColumnIds.length) return true;
      return stagedColumnIds.some((id, i) => id !== orderedVisibleColumnIds[i]);
    }, [stagedColumnIds, orderedVisibleColumnIds]);

    const isSortingDirty = useMemo(() => {
      const current = sorting ?? [];
      if (stagedSorting.length !== current.length) return true;
      return stagedSorting.some((s, i) => {
        const c = current[i];
        return !c || s.id !== c.id || s.desc !== c.desc;
      });
    }, [stagedSorting, sorting]);

    const isFiltersDirty = useMemo(
      () => !vitalsFiltersAreEqual(stagedFilters, activeFilters),
      [stagedFilters, activeFilters]
    );

    const isDirty = providedIsDirty || isColumnsDirty || isFiltersDirty || isSortingDirty;

    const handleApply = () => {
      onApplyConfig(stagedColumnIds, stagedFilters, isSortingDirty ? stagedSorting : undefined);
      onClose();
    };

    return (
      <>
        <div className={cn("flex-1 overflow-y-auto space-y-6 p-4", !isDirty && "mb-2")}>
          <div className="space-y-2">
            <span
              className={cn(
                "flex gap-2 items-center font-medium text-sm",
                !!stagedFilters.dateRange && "text-green-600"
              )}
            >
              <Calendar size={16} />
              {t("Date Range")}
            </span>
            <DateRangeField
              value={stagedFilters.dateRange ?? {}}
              onChange={(val) =>
                setStagedFilters((prev) => ({
                  ...prev,
                  dateRange: Object.keys(val).length > 0 ? val : undefined,
                }))
              }
            />
          </div>
          <ColumnConfigForm
            columnVisibility={columnVisibility}
            orderedVisibleColumnIds={orderedVisibleColumnIds}
            combinedFieldToColumnConfigMap={combinedFieldToColumnConfigMap}
            stagedColumnIds={stagedColumnIds}
            onStagedColumnIdsChange={setStagedColumnIds}
            title={columnConfigTitle}
            stagedSorting={setSorting ? stagedSorting : undefined}
            onStagedSortingChange={setSorting ? setStagedSorting : undefined}
            isDirty={isColumnsDirty || isSortingDirty}
            reset={() => {
              setStagedSorting(sorting ?? []);
              setStagedColumnIds(orderedVisibleColumnIds);
            }}
          />
          <VitalsFilterConfigForm
            stagedFilters={stagedFilters}
            onStagedFiltersChange={setStagedFilters}
            sourceOptions={sourceOptions}
          />
        </div>
        {isDirty && (
          <DialogFooter className="border-t p-4 bg-gray-100 justify-end">
            {onClearAll && (
              <Button variant="outline" onClick={onClearAll} data-testid="table-config-clear-all-button">
                {t("Clear All")}
              </Button>
            )}
            <Button variant="outline" onClick={onClose} data-testid="table-config-cancel-button">
              {t("Cancel")}
            </Button>
            <Button onClick={handleApply} data-testid="table-config-apply-button">
              {t("Apply Filters")}
            </Button>
          </DialogFooter>
        )}
      </>
    );
  },
  ({ open }) => `${open}-vitals-table-config-dialog-content`
);
